import React, { createContext, useContext, useState, useEffect } from 'react';

// Language translations
const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      mortgages: 'Mortgages',
      loans: 'Loans',
      insurance: 'Insurance',
      propertyInvestment: 'Property Investment',
      help: 'Help',
      contact: 'Contact',
      clientLogin: 'Client Login',
      getQuote: 'Get a Quote',
    },
    // Hero
    hero: {
      badge: 'Trusted by 10,000+ clients',
      titleLine1: 'Make your',
      titleLine2: 'home dreams',
      titleLine3: 'a reality',
      subtitle: 'Expert mortgage advice from a broker you can trust. We compare 100+ lenders to find you the best rates.',
      exploreMortgages: 'Explore Mortgages',
      getApp: 'Get our App',
    },
    // Calculator
    calculator: {
      title: 'Quick Quote Calculator',
      liveRates: 'Live Rates',
      propertyValue: 'Property Value',
      deposit: 'Deposit',
      estimatedMonthly: 'Estimated Monthly Payment',
      perMonth: '/mo',
      basedOn: 'Based on 4.5% over 25 years',
      getPersonalisedQuote: 'Get Personalised Quote →',
    },
    // Partners
    partners: {
      title: 'Trusted partners · We compare 100+ lenders',
    },
    // Services
    services: {
      title: 'Your Credys experience',
      titleHighlight: 'starts here',
      subtitle: 'See how we can help you with mortgages, loans, insurance and more.',
      residential: {
        title: 'Residential Mortgages',
        description: 'Find a mortgage that works for you.',
        link: 'View mortgages →',
      },
      btl: {
        title: 'Buy-to-Let',
        description: 'Portfolio landlords and investors.',
        link: 'View BTL options →',
      },
      loans: {
        title: 'Personal Loans',
        description: 'Competitive rates, quick decisions.',
        link: 'View loans →',
      },
      remortgage: {
        title: 'Remortgage',
        description: 'Save money or release equity.',
        link: 'View remortgage →',
      },
      lifeInsurance: {
        title: 'Life Insurance',
        description: 'Protect what matters most.',
        link: 'View insurance →',
      },
      commercial: {
        title: 'Commercial',
        description: 'Business loans and mortgages.',
        link: 'View commercial →',
      },
      homeInsurance: {
        title: 'Home Insurance',
        description: 'Buildings and contents cover.',
        link: 'View insurance →',
      },
      firstTimeBuyers: {
        title: 'First Time Buyers',
        description: 'Special support for your first home.',
        link: 'Get started →',
      },
    },
    // Property Investment
    investment: {
      badge: 'Property Investment',
      title: 'Build your property',
      titleHighlight: 'portfolio',
      subtitle: 'Whether you\'re a first-time investor or expanding your portfolio, we provide expert guidance on buy-to-let mortgages, HMO financing, and commercial property investment.',
      stats: {
        btlMortgages: 'BTL Mortgages Arranged',
        investmentProperties: 'Investment Properties',
        averageYield: 'Average Yield Achieved',
        dipDecision: 'Decision in Principle',
      },
      exploreOptions: 'Explore Investment Options',
      freeConsultation: 'Free Consultation',
      calculatorTitle: 'Investment Calculator',
      purchasePrice: 'Property Purchase Price',
      monthlyRent: 'Expected Monthly Rent',
      grossYield: 'Gross Rental Yield',
      annualIncome: 'Est. Annual Income',
      getBTLQuote: 'Get BTL Quote →',
    },
    // Video Testimonials
    testimonials: {
      title: 'Hear from our',
      titleHighlight: 'clients',
      subtitle: 'Real stories from real people we\'ve helped',
    },
    // FAQ
    faq: {
      title: 'Frequently asked',
      titleHighlight: 'questions',
      subtitle: 'Everything you need to know about our services',
      stillHaveQuestions: 'Still have questions?',
      contactUs: 'Contact Us',
      questions: {
        deposit: {
          q: 'How much deposit do I need for a mortgage?',
          a: 'Typically, you\'ll need at least 5-10% of the property value as a deposit. However, larger deposits (15-20%) often secure better interest rates. We can help you explore options even with smaller deposits.',
        },
        processTime: {
          q: 'How long does the mortgage process take?',
          a: 'From application to completion, the process typically takes 4-8 weeks. We can often get a Decision in Principle within 24 hours, giving you confidence when making offers on properties.',
        },
        fees: {
          q: 'Do you charge fees for your services?',
          a: 'Our initial consultation is completely free. We\'re transparent about fees upfront – we may charge a broker fee depending on the complexity of your case, but we\'ll always discuss this before proceeding.',
        },
        badCredit: {
          q: 'Can you help with bad credit mortgages?',
          a: 'Yes! We have access to specialist lenders who work with clients who have had credit difficulties. We\'ll assess your situation and find the best options available to you.',
        },
      },
    },
    // CTA
    cta: {
      title: 'Ready to get started?',
      subtitle: 'Get a free, no-obligation quote in minutes. Our expert advisors are ready to help.',
      getFreeQuote: 'Get Free Quote',
    },
    // Footer
    footer: {
      description: 'Your home may be repossessed if you do not keep up repayments.',
      products: 'Products',
      company: 'Company',
      contact: 'Contact',
      aboutUs: 'About Us',
      careers: 'Careers',
      blog: 'Blog',
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
      copyright: '© 2025 Credys Financial Services. All rights reserved.',
    },
    // Chat Widget
    chat: {
      tooltip: '👋 Need help? Chat with us!',
    },
    // Portal
    portal: {
      dashboard: 'Dashboard',
      myApplications: 'My Applications',
      documents: 'Documents',
      messages: 'Messages',
      payments: 'Payments',
      helpCentre: 'Help Centre',
      settings: 'Settings',
      welcomeBack: 'Welcome back',
      applicationInProgress: 'Application In Progress',
      goodMorning: 'Good morning',
      continueApplication: 'Continue Application →',
      applications: 'Applications',
      documentsNeeded: 'Documents Needed',
      stepsComplete: 'Steps Complete',
      newMessage: 'New Message',
      applicationProgress: 'Application Progress',
      overallProgress: 'Overall Progress',
      complete: 'Complete',
      requiredActions: 'Required Actions',
      yourAdvisor: 'Your Advisor',
      online: 'Online',
      sendMessage: 'Send Message',
      recentActivity: 'Recent Activity',
      viewAll: 'View All →',
      viewFullDetails: 'View Full Details',
    },
  },
  tr: {
    // Navigation
    nav: {
      home: 'Ana Sayfa',
      mortgages: 'Mortgage',
      loans: 'Krediler',
      insurance: 'Sigorta',
      propertyInvestment: 'Gayrimenkul Yatırımı',
      help: 'Yardım',
      contact: 'İletişim',
      clientLogin: 'Müşteri Girişi',
      getQuote: 'Teklif Al',
    },
    // Hero
    hero: {
      badge: '10.000+ müşteri tarafından güveniliyor',
      titleLine1: 'Ev hayallerinizi',
      titleLine2: 'gerçeğe',
      titleLine3: 'dönüştürün',
      subtitle: 'Güvenebileceğiniz bir danışmandan uzman mortgage tavsiyesi. Size en iyi oranları bulmak için 100+ kredi veren kurumu karşılaştırıyoruz.',
      exploreMortgages: 'Mortgage\'leri Keşfet',
      getApp: 'Uygulamamızı İndir',
    },
    // Calculator
    calculator: {
      title: 'Hızlı Teklif Hesaplayıcı',
      liveRates: 'Canlı Oranlar',
      propertyValue: 'Mülk Değeri',
      deposit: 'Peşinat',
      estimatedMonthly: 'Tahmini Aylık Ödeme',
      perMonth: '/ay',
      basedOn: '25 yıl boyunca %4,5 oranına göre',
      getPersonalisedQuote: 'Kişisel Teklif Al →',
    },
    // Partners
    partners: {
      title: 'Güvenilir ortaklar · 100+ kredi vereni karşılaştırıyoruz',
    },
    // Services
    services: {
      title: 'Credys deneyiminiz',
      titleHighlight: 'burada başlıyor',
      subtitle: 'Mortgage, kredi, sigorta ve daha fazlasında size nasıl yardımcı olabileceğimizi görün.',
      residential: {
        title: 'Konut Mortgage\'leri',
        description: 'Size uygun bir mortgage bulun.',
        link: 'Mortgage\'leri gör →',
      },
      btl: {
        title: 'Kiraya Ver',
        description: 'Portföy ev sahipleri ve yatırımcılar.',
        link: 'BTL seçeneklerini gör →',
      },
      loans: {
        title: 'Bireysel Krediler',
        description: 'Rekabetçi oranlar, hızlı kararlar.',
        link: 'Kredileri gör →',
      },
      remortgage: {
        title: 'Yeniden Mortgage',
        description: 'Para biriktirin veya sermaye çıkarın.',
        link: 'Yeniden mortgage\'i gör →',
      },
      lifeInsurance: {
        title: 'Hayat Sigortası',
        description: 'En önemli şeyleri koruyun.',
        link: 'Sigortayı gör →',
      },
      commercial: {
        title: 'Ticari',
        description: 'İşletme kredileri ve mortgage\'ler.',
        link: 'Ticari seçenekleri gör →',
      },
      homeInsurance: {
        title: 'Ev Sigortası',
        description: 'Bina ve eşya sigortası.',
        link: 'Sigortayı gör →',
      },
      firstTimeBuyers: {
        title: 'İlk Kez Alıcılar',
        description: 'İlk eviniz için özel destek.',
        link: 'Başla →',
      },
    },
    // Property Investment
    investment: {
      badge: 'Gayrimenkul Yatırımı',
      title: 'Mülk portföyünüzü',
      titleHighlight: 'oluşturun',
      subtitle: 'İster ilk kez yatırımcı olun ister portföyünüzü genişletin, kiraya ver mortgage\'leri, HMO finansmanı ve ticari gayrimenkul yatırımı konusunda uzman rehberlik sağlıyoruz.',
      stats: {
        btlMortgages: 'Kiraya Ver Mortgage Düzenlendi',
        investmentProperties: 'Yatırım Mülkleri',
        averageYield: 'Ortalama Getiri',
        dipDecision: 'İlke Kararı',
      },
      exploreOptions: 'Yatırım Seçeneklerini Keşfet',
      freeConsultation: 'Ücretsiz Danışmanlık',
      calculatorTitle: 'Yatırım Hesaplayıcı',
      purchasePrice: 'Mülk Satın Alma Fiyatı',
      monthlyRent: 'Beklenen Aylık Kira',
      grossYield: 'Brüt Kira Getirisi',
      annualIncome: 'Tah. Yıllık Gelir',
      getBTLQuote: 'BTL Teklifi Al →',
    },
    // Video Testimonials
    testimonials: {
      title: 'Müşterilerimizden',
      titleHighlight: 'dinleyin',
      subtitle: 'Yardım ettiğimiz gerçek insanlardan gerçek hikayeler',
    },
    // FAQ
    faq: {
      title: 'Sıkça sorulan',
      titleHighlight: 'sorular',
      subtitle: 'Hizmetlerimiz hakkında bilmeniz gereken her şey',
      stillHaveQuestions: 'Hâlâ sorularınız mı var?',
      contactUs: 'Bize Ulaşın',
      questions: {
        deposit: {
          q: 'Mortgage için ne kadar peşinat gerekiyor?',
          a: 'Genellikle, mülk değerinin en az %5-10\'u kadar peşinat ödemeniz gerekir. Ancak, daha büyük peşinatlar (%15-20) genellikle daha iyi faiz oranları sağlar. Daha küçük peşinatlarla bile seçenekleri keşfetmenize yardımcı olabiliriz.',
        },
        processTime: {
          q: 'Mortgage süreci ne kadar sürer?',
          a: 'Başvurudan tamamlanmaya kadar süreç genellikle 4-8 hafta sürer. Genellikle 24 saat içinde İlke Kararı alabiliriz, bu da mülklere teklif verirken size güven verir.',
        },
        fees: {
          q: 'Hizmetleriniz için ücret alıyor musunuz?',
          a: 'İlk danışmamız tamamen ücretsizdir. Ücretler konusunda şeffafız – durumunuzun karmaşıklığına bağlı olarak bir komisyoncu ücreti alabiliriz, ancak devam etmeden önce bunu her zaman tartışırız.',
        },
        badCredit: {
          q: 'Kötü kredi geçmişi olan mortgage\'lere yardımcı olabilir misiniz?',
          a: 'Evet! Kredi güçlükleri yaşamış müşterilerle çalışan uzman kredi verenlerine erişimimiz var. Durumunuzu değerlendirip size en uygun seçenekleri bulacağız.',
        },
        turkish: {
          q: 'Türkçe konuşan danışman var mı?',
          a: 'Evet! Türkçe konuşan danışmanlarımız var. Size kendi dilinizde yardımcı olmaktan mutluluk duyarız. Hemen bizimle iletişime geçin.',
        },
      },
    },
    // CTA
    cta: {
      title: 'Başlamaya hazır mısınız?',
      subtitle: 'Dakikalar içinde ücretsiz, zorunlu olmayan bir teklif alın. Uzman danışmanlarımız yardıma hazır.',
      getFreeQuote: 'Ücretsiz Teklif Al',
    },
    // Footer
    footer: {
      description: 'Ödemelerinizi yapmazsanız evinize el konulabilir.',
      products: 'Ürünler',
      company: 'Şirket',
      contact: 'İletişim',
      aboutUs: 'Hakkımızda',
      careers: 'Kariyer',
      blog: 'Blog',
      privacy: 'Gizlilik',
      terms: 'Şartlar',
      cookies: 'Çerezler',
      copyright: '© 2025 Credys Financial Services. Tüm hakları saklıdır.',
    },
    // Chat Widget
    chat: {
      tooltip: '👋 Yardıma mı ihtiyacınız var? Bizimle sohbet edin!',
    },
    // Portal
    portal: {
      dashboard: 'Kontrol Paneli',
      myApplications: 'Başvurularım',
      documents: 'Belgeler',
      messages: 'Mesajlar',
      payments: 'Ödemeler',
      helpCentre: 'Yardım Merkezi',
      settings: 'Ayarlar',
      welcomeBack: 'Tekrar hoş geldiniz',
      applicationInProgress: 'Devam Eden Başvuru',
      goodMorning: 'Günaydın',
      continueApplication: 'Başvuruya Devam Et →',
      applications: 'Başvurular',
      documentsNeeded: 'Gereken Belgeler',
      stepsComplete: 'Tamamlanan Adımlar',
      newMessage: 'Yeni Mesaj',
      applicationProgress: 'Başvuru İlerlemesi',
      overallProgress: 'Genel İlerleme',
      complete: 'Tamamlandı',
      requiredActions: 'Gerekli İşlemler',
      yourAdvisor: 'Danışmanınız',
      online: 'Çevrimiçi',
      sendMessage: 'Mesaj Gönder',
      recentActivity: 'Son Aktivite',
      viewAll: 'Tümünü Gör →',
      viewFullDetails: 'Tüm Detayları Gör',
    },
  },
};

// Language Context
const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('credys-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('credys-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'tr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default translations;
